import { type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import * as goalsApi from "@/lib/api/flow-instances/goals";

// --- Query Keys ---

const keys = {
  goals: (instanceId: string | null) => ["flowInstances", instanceId, "goals"],
  goal: (goalId: string | null) => ["flowInstances", "goals", goalId],
  availableProducts: (instanceId: string | null) => [
    "flowInstances",
    instanceId,
    "availableProducts",
  ],
  productsSuitability: (goalId: string | null) => [
    "flowInstances",
    "goals",
    goalId,
    "productsSuitability",
  ],
  timeHorizons: (riskModelId: string | null) => [
    "flowInstances",
    "reference",
    "timeHorizons",
    riskModelId,
  ],
  riskLevels: (riskModelId: string | null) => [
    "flowInstances",
    "reference",
    "riskLevels",
    riskModelId,
  ],
  portfolios: (collectionId: string | null) => [
    "flowInstances",
    "reference",
    "portfolios",
    collectionId,
  ],
  suitabilityContext: (goalId: string | null) => [
    "flowInstances",
    "goals",
    goalId,
    "suitabilityContext",
  ],
  riskModelOptions: (goalId: string | null) => [
    "flowInstances",
    "goals",
    goalId,
    "riskModelOptions",
  ],
};

// --- Query Hooks ---

export function useFlowInstanceGoals(instanceId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.goals(instanceId),
    queryFn: () => goalsApi.getGoals(instanceId!),
    enabled: !!instanceId,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: keys.goals(instanceId) });
  return { goals: data ?? [], error, isLoading, mutate };
}

export function useGoal(goalId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.goal(goalId),
    queryFn: () => goalsApi.getGoal(goalId!),
    enabled: !!goalId,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: keys.goal(goalId) });
  return { goal: data ?? null, error, isLoading, mutate };
}

export function useProductsWithSuitability(goalId: string | null) {
  const { data, error, isLoading, isSuccess } = useQuery({
    queryKey: keys.productsSuitability(goalId),
    queryFn: () => goalsApi.getProductsWithSuitability(goalId!),
    enabled: !!goalId,
  });
  // QL-502 review #1 — `products` folds a not-yet-fetched/disabled/errored
  // query into `[]`, which is indistinguishable from "this goal truly has no
  // suitable products" to a caller that only reads `products`. Expose
  // `isSuccess` (true only once the query actually resolved) so a caller that
  // must tell "no data yet" apart from "confirmed empty" — like the builder's
  // locked-holdings derivation — can do so without guessing from `[]`.
  return { products: data ?? [], error, isLoading, isSuccess };
}

export function useSuitabilityContext(goalId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.suitabilityContext(goalId),
    queryFn: () => goalsApi.getSuitabilityContext(goalId!),
    enabled: !!goalId,
  });
  return { context: data ?? null, error, isLoading };
}

// --- Mutation Hooks ---

// --- Custom Product Mutations ---

// --- Exported Query Keys (for targeted invalidation in widget mutations) ---

export { keys as goalQueryKeys };

/**
 * Invalidate every goal's suitability inputs (context + product suitability).
 * Components whose answers change the verdict — product assessment,
 * classification, risk — call this so the portfolio builder's product filter,
 * "Suitable" badges and submit-blocker recompute without a page remount.
 *
 * The keys live under ["flowInstances","goals",goalId,("suitabilityContext"|
 * "productsSuitability")], so a plain `["suitability"]` invalidation (used
 * historically) matched nothing.
 */
export function invalidateSuitabilityQueries(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const k = query.queryKey;
      return (
        Array.isArray(k) &&
        k[0] === "flowInstances" &&
        k[1] === "goals" &&
        (k[3] === "suitabilityContext" || k[3] === "productsSuitability")
      );
    },
  });
}
